import type { FC } from "react";
import { createElement, createRef } from "react";
import { useNavigate } from "react-router-dom";

import { shallowEqual } from "shallow-equal";

import { languageSelector } from "@/src/contexts";
import {
    type IShoppingMallTileResources,
    ShoppingMallTileResources,
} from "@/src/shopping-mall/ShoppingMallTile.Resources";
import { ShoppingMallTileResourcesEn } from "@/src/shopping-mall/ShoppingMallTile.Resources.En";
import type { IComponent } from "@Components/Component";
import { Component, defaultAttributes } from "@Components/Component";
import type { Output } from "@Components/Hooks/useAjax";
import ImageLoader, { type IImageLoader } from "@Components/ImageLoader";
import { Loading } from "@Components/Loading";
import type { IMapMarker } from "@Components/Map";
import type { AjaxFail, AjaxSuccess } from "@Components/Modules/Ajax";
import { Ajax } from "@Components/Modules/Ajax";
import type { TelemetryProps } from "@Components/Modules/Helpers";
import { urlFormat } from "@Components/Modules/Helpers";
import { mainContainerId } from "@Constants/PortalConstants";
import * as stylex from "@stylexjs/stylex";

import { genericStyles } from "../../styles/tokens.stylex";
import { TransitionLink } from "../common/Transition";
import LocationIcon from "../common/icons/LocationIcon";
import FavoriteLink from "../common/links/FavoriteLink";

import * as css from "./ShoppingMallTile.module.css";

export interface IDescriptionElement {
    h3: HTMLHeadElement;
    h4: HTMLHeadElement;
}

export interface IShoppingMallSaveEvents {
    saveSuccess?: AjaxSuccess<Output<boolean>>;
    saveFail?: AjaxFail;
}

export interface IShoppingMallTile extends IComponent, TelemetryProps {
    id: string;
    shouldComponentUpdate?: boolean;
    onscreenTrack?: string;
    descriptionElement?: keyof IDescriptionElement;
    image?: IImageLoader;
    title?: string;
    description?: string | JSX.Element;
    logo?: IImageLoader;
    navigateUrl?: string;
    trackerAction?: string;
    showSave?: boolean;
    isLoadingLayout?: boolean;
    mapLink: string;
    onMapLinkClick: (event: React.MouseEvent<HTMLDivElement>, mapMarker: IMapMarker) => void;
    mapMarker: IMapMarker;
    anchor: string;
    pageTransition: (url: string) => void;
    resources?: IShoppingMallTileResources;
}

class ShoppingMallTileBase extends Component<IShoppingMallTile> {
    private onScreenTrackTimeout: number | null = null;

    private mainRect: ClientRect | null = null;

    protected containerRef = createRef<HTMLDivElement>();

    private onScreenTrackRequest: XMLHttpRequest | undefined;

    private ajax: Ajax<void> | null = null;

    constructor(props: IShoppingMallTile) {
        super(props);

        if (props.onscreenTrack) {
            this.ajax = new Ajax({ ajaxAction: urlFormat(props.onscreenTrack, this.props) });
            this.ajax.success = () => {
                this.onScreenTrackRequest = undefined;
            };
        }
    }

    public componentDidMount() {
        if (!this.props.onscreenTrack) {
            return;
        }

        window.addEventListener("scroll", this.onScreenTrack);
        window.addEventListener("resize", this.updateMainContainer);
        this.updateMainContainer();
    }

    public shouldComponentUpdate(nextProps: IShoppingMallTile): boolean {
        if (!this.props.shouldComponentUpdate) {
            return false;
        }

        return !shallowEqual(this.props, nextProps);
    }

    public componentWillUnmount() {
        if (this.props.onscreenTrack) {
            if (this.onScreenTrackTimeout) {
                clearTimeout(this.onScreenTrackTimeout);
            }

            window.removeEventListener("scroll", this.onScreenTrack);
            window.removeEventListener("resize", this.updateMainContainer);

            if (this.onScreenTrackRequest) {
                this.onScreenTrackRequest.abort();
                this.onScreenTrackRequest = undefined;
            }
        }
    }

    protected get description() {
        if (!this.props.resources.mapLinkText) {
            return (
                this.props.description &&
                createElement(
                    this.props.descriptionElement || "h4",
                    { className: css.description },
                    this.props.description,
                )
            );
        }

        return (
            <button className={css.location} onClick={this.onClick} type="button">
                <LocationIcon />
                <span className={css.goToText}>{this.props.resources.mapLinkText}</span>
            </button>
        );
    }

    protected get logo() {
        return (
            this.props.logo && (
                <div className={css.shoppingMallLogo}>
                    <ImageLoader {...this.props.logo} />
                </div>
            )
        );
    }

    private sendOnScreenTrack = (): void => {
        if (!this.mainRect) {
            return;
        }

        if (!this.containerRef.current) {
            return;
        }

        const rect = this.containerRef.current.getBoundingClientRect();

        if (rect.top < 0 || rect.top > (window.innerHeight || document.documentElement.clientHeight)) {
            return;
        }

        if (rect.bottom > (window.innerHeight || document.documentElement.clientHeight)) {
            return;
        }

        if (rect.left < this.mainRect.left || rect.right > this.mainRect.right) {
            return;
        }

        if (this.onScreenTrackRequest) {
            this.onScreenTrackRequest.abort();
            this.onScreenTrackRequest = undefined;
        }

        if (this.ajax) {
            this.onScreenTrackRequest = this.ajax.request();
        }
    };

    private updateMainContainer = () => {
        const mainContainer = document.getElementById(mainContainerId);
        if (mainContainer) {
            this.mainRect = mainContainer.getBoundingClientRect();
        }

        this.sendOnScreenTrack();
    };

    private onScreenTrack = () => {
        if (this.onScreenTrackTimeout) {
            clearTimeout(this.onScreenTrackTimeout);
        }

        this.onScreenTrackTimeout = window.setTimeout(this.sendOnScreenTrack, 600);
    };

    private onClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        if (this.props.mapLink) {
            event.stopPropagation();
            event.preventDefault();

            this.props.pageTransition(
                this.props.mapLink + (this.props.anchor !== null ? `#${this.props.anchor}` : ""),
            );
        } else if (this.props.onMapLinkClick) {
            event.stopPropagation();
            event.preventDefault();

            this.props.onMapLinkClick.call(this, event, this.props.mapMarker);
        }
    };

    private renderImage() {
        if (!this.props.image) {
            return null;
        }

        return (
            <div className={css.shoppingMallImage}>
                <ImageLoader {...this.props.image} className={css.image} alt={this.props.title} />
            </div>
        );
    }

    private renderDetails() {
        return (
            <div className={css.shoppingMallDetails}>
                <div className={css.shoppingMallDescription}>
                    {this.props.title && <h3 className={css.title}>{this.props.title}</h3>}
                    {this.description}
                </div>
                {this.logo}
            </div>
        );
    }

    protected renderSave() {
        return (
            this.props.showSave !== false && (
                <FavoriteLink id={this.props.id} showText={false} favoriteLinkIconSize="big" type="malls" />
            )
        );
    }

    public render() {
        if (this.props.isLoadingLayout === true) {
            return (
                <div {...defaultAttributes(this.props, css.loadingLayout)}>
                    <Loading isModal noModalLayer />
                </div>
            );
        }

        return (
            <div
                {...defaultAttributes(
                    this.props,
                    css.shoppingMallTileComponent,
                    stylex.props(styles.border).className,
                )}
                ref={this.containerRef}
            >
                <TransitionLink
                    className={css.linkWrapper}
                    attrTitle={this.props.title}
                    navigateUrl={this.props.navigateUrl}
                    trackingUrl={this.props.trackerAction}
                    ajaxIgnoreError
                    hideLoading
                >
                    {this.renderImage()}
                    {this.renderDetails()}
                </TransitionLink>
                {this.renderSave()}
            </div>
        );
    }
}

export const ShoppingMallTile: FC<IShoppingMallTile> = (props) => {
    const navigate = useNavigate();
    const resources = languageSelector(ShoppingMallTileResources, ShoppingMallTileResourcesEn);
    return <ShoppingMallTileBase {...props} resources={resources} pageTransition={navigate} />;
};

const styles = stylex.create({
    border: {
        borderRadius: genericStyles.borderRadiusS1,
    },
});
